import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import type { Resolver, UseFormReturn } from 'react-hook-form';
import { useForm } from 'react-hook-form';

import { useOpenNotification } from 'utils/antd/notification-toasts';

import type { IntegrationValidationError } from 'api';
import {
  getGetIntegrationBusinessUnitsQueryKey,
  usePostIntegrationBusinessUnits
} from 'api';
import type { CreateBusinessUnitFormValues } from 'validators/businessUnit';
import { createBusinessUnitSchema } from 'validators/businessUnit';

import { useOrganisationId } from 'hooks/context';

import {
  CREATE_BUSINESS_UNIT_GENERIC_ERROR,
  CREATE_BUSINESS_UNIT_SUCCESS
} from './constants';

import { extractIntegrationValidationError } from '../../utils/extractIntegrationValidationError';
import { useBusinessUnitsListSync } from '../BusinessUnitsListSyncContext';

type UseCreateBusinessUnitFormArgs = {
  onClose: () => void;
};

export type UseCreateBusinessUnitForm = {
  form: UseFormReturn<CreateBusinessUnitFormValues>;
  submit: ReturnType<
    UseFormReturn<CreateBusinessUnitFormValues>['handleSubmit']
  >;
  cancel: () => void;
  isSubmitting: boolean;
};

export const useCreateBusinessUnitForm = ({
  onClose
}: UseCreateBusinessUnitFormArgs): UseCreateBusinessUnitForm => {
  const organisationId = useOrganisationId<number>();
  const queryClient = useQueryClient();
  const openNotification = useOpenNotification();
  const { onBusinessUnitCreated } = useBusinessUnitsListSync();
  const createMutation = usePostIntegrationBusinessUnits();

  const form = useForm<CreateBusinessUnitFormValues>({
    mode: 'onChange',
    resolver: yupResolver(
      createBusinessUnitSchema
    ) as unknown as Resolver<CreateBusinessUnitFormValues>,
    defaultValues: { name: '' }
  });

  const handleSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: getGetIntegrationBusinessUnitsQueryKey(organisationId)
    });
    onBusinessUnitCreated();
    openNotification({
      kind: 'success',
      message: CREATE_BUSINESS_UNIT_SUCCESS
    });
    cancel();
  };

  const handleError = (error: IntegrationValidationError | null) => {
    openNotification({
      kind: 'error',
      message: extractIntegrationValidationError(
        error,
        CREATE_BUSINESS_UNIT_GENERIC_ERROR
      )
    });
  };

  const submit = form.handleSubmit((values) => {
    createMutation.mutate(
      { organisationId, data: { name: values.name.trim() } },
      { onSuccess: handleSuccess, onError: handleError }
    );
  });

  const cancel = () => {
    form.reset();
    onClose();
  };

  return {
    form,
    submit,
    cancel,
    isSubmitting: createMutation.isPending
  };
};
